// Phase 4r — "Did you mean…" support for the search-results page.
// Implements a small normalized-Levenshtein scorer used client-side
// when a query returns zero matches: we compare the query against
// known type names + category names from the already-cached catalog,
// surface up to three candidates whose distance falls under a budget
// proportional to the query length.
//
// Why client-side: catalog cardinality is bounded (the seed file caps
// the staging fleet at 8 types across 4 categories, and even an
// expansion to 100 types is trivial to score in JS). Going to the
// server would buy a fuzzy-trigram path the customer never sees on
// the happy-path search, and MySQL has no native trigram. If the
// catalog ever crosses ~500 rows the right move is a `pg_trgm`
// migration, not a Ruby Levenshtein.

import type { CategorySummary, TypeSummary } from "./types";

export interface Suggestion {
  // The raw label to show the customer ("Bobcat S70" or "Skid steers").
  label: string;
  // The URL the suggestion chip should link to. Type suggestions deep-link
  // to /search?q=<label>; category suggestions deep-link to /c/<slug>
  // because that's the page that actually filters by category.
  href: string;
  kind: "type" | "category";
}

// Classic iterative Levenshtein with O(n) memory. Used for the rare
// zero-result path so micro-perf doesn't matter; readability does.
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);

  for (let j = 0; j <= b.length; j += 1) prev[j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,        // deletion
        curr[j - 1] + 1,    // insertion
        prev[j - 1] + cost  // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
}

// "bocat" vs "bobcat s70" — score the candidate as the BEST distance
// from the query to either the full candidate or any single whitespace
// token. This keeps "bocat" → "Bobcat S70" working (token-best beats
// whole-string) while not over-suggesting on partial matches against
// long multi-word names.
function bestDistance(queryNorm: string, candidateRaw: string): number {
  const norm = normalize(candidateRaw);
  if (norm.length === 0) return Number.POSITIVE_INFINITY;
  const tokens = norm.split(" ");
  let best = levenshtein(queryNorm, norm);
  for (const tok of tokens) {
    const d = levenshtein(queryNorm, tok);
    if (d < best) best = d;
  }
  return best;
}

// Distance budget: ≤ ~40% of the query length, rounded down, with a
// minimum of 1 so 3-char queries still get a 1-edit fuzziness budget.
// Caps at 4 so 12-char queries don't trigger off-the-wall suggestions.
export function distanceBudget(query: string): number {
  const len = normalize(query).length;
  if (len === 0) return 0;
  return Math.max(1, Math.min(4, Math.floor(len * 0.4)));
}

export function buildSuggestions(
  query: string,
  types: TypeSummary[],
  categories: CategorySummary[],
  limit = 3
): Suggestion[] {
  const queryNorm = normalize(query);
  if (queryNorm.length === 0) return [];
  const budget = distanceBudget(query);

  // Score every candidate (types + categories) then keep the best few
  // under-budget hits. We dedupe by label so a category named
  // "Skid Steers" doesn't appear twice if a type also includes the
  // phrase.
  const scored: Array<{ s: Suggestion; d: number }> = [];

  for (const t of types) {
    const d = bestDistance(queryNorm, t.name);
    if (d <= budget) {
      scored.push({
        s: { label: t.name, href: `/search?q=${encodeURIComponent(t.name)}`, kind: "type" },
        d
      });
    }
  }

  for (const c of categories) {
    const d = bestDistance(queryNorm, c.name);
    if (d <= budget) {
      scored.push({
        s: { label: c.name, href: `/c/${c.slug}`, kind: "category" },
        d
      });
    }
  }

  scored.sort((a, b) => a.d - b.d);

  const seen = new Set<string>();
  const out: Suggestion[] = [];
  for (const { s } of scored) {
    const key = s.label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length >= limit) break;
  }
  return out;
}
